import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { 
  Upload, 
  FileText, 
  X, 
  Image as ImageIcon,
  Download,
  Plus,
  AlertCircle,
  CheckCircle,
  Camera
} from 'lucide-react';
import { apiService } from '../../services/api';

export const UniversalUpload = ({ 
  entityId, 
  entityType, // 'cliente', 'moto', 'manutencao'
  allowedTypes = ['CNH', 'RG', 'Foto', 'Documento', 'Comprovante'],
  showProfilePicture = false,
  onProfilePictureSet = null,
  onUploadSuccess = null,
  acceptedTypes = null,
  showPreview = false,
  className = ''
}) => {
  const [arquivos, setArquivos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  
  // Estados para upload
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [tipoDocumento, setTipoDocumento] = useState('');
  const [descricao, setDescricao] = useState('');

  useEffect(() => {
    if (entityId) {
      carregarArquivos();
    }
  }, [entityId]);

  const carregarArquivos = async () => {
    if (!entityId) return;
    
    try {
      let arquivosData = [];
      switch(entityType) {
        case 'cliente':
          arquivosData = await apiService.getArquivosCliente(entityId);
          break;
        case 'moto':
          arquivosData = await apiService.getArquivosMoto(entityId);
          break;
        case 'manutencao':
          arquivosData = await apiService.getArquivosManutencao(entityId);
          break;
      }
      
      setArquivos(arquivosData);
      
      // Identificar foto de perfil
      const fotoPerfil = arquivosData.find(arquivo => 
        arquivo.tipo === 'Foto' && arquivo.is_profile === true
      );
      if (fotoPerfil) {
        setProfilePicture(fotoPerfil);
        if (onProfilePictureSet) {
          onProfilePictureSet(fotoPerfil.url);
        }
      }
      
    } catch (error) {
      console.error('Erro ao carregar arquivos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (selectedFiles.length === 0 || !tipoDocumento) {
      alert('Selecione arquivos e tipo de documento');
      return;
    }

    setUploading(true);
    try {
      let lastUploadResponse;
      for (const file of selectedFiles) {
        let uploadResponse;
        switch(entityType) {
          case 'cliente':
            uploadResponse = await apiService.uploadArquivoCliente(entityId, file, tipoDocumento, descricao);
            break;
          case 'moto':
            uploadResponse = await apiService.uploadArquivoMoto(entityId, file, tipoDocumento, descricao);
            break;
          case 'manutencao':
            uploadResponse = await apiService.uploadArquivoManutencao(entityId, file, tipoDocumento, descricao);
            break;
        }
        lastUploadResponse = uploadResponse;
      }
      
      alert('Arquivos enviados com sucesso!');
      setSelectedFiles([]);
      setTipoDocumento('');
      setDescricao('');
      setShowUploadModal(false);
      carregarArquivos();
      
      // Chamar callback se existir (para fotos de perfil)
      if (onUploadSuccess && lastUploadResponse) {
        onUploadSuccess(lastUploadResponse);
      }
      
    } catch (error) {
      console.error('Erro no upload:', error);
      alert('Erro ao enviar arquivos. Tente novamente.');
    } finally {
      setUploading(false);
    }
  };

  const setAsProfilePicture = async (arquivo) => {
    try {
      await apiService.setProfilePicture(entityType, entityId, arquivo.id);
      setProfilePicture(arquivo);
      if (onProfilePictureSet) {
        onProfilePictureSet(arquivo.url);
      }
      alert('Foto de perfil definida com sucesso!');
      carregarArquivos();
    } catch (error) {
      console.error('Erro ao definir foto de perfil:', error);
      alert('Erro ao definir foto de perfil');
    }
  };

  const handleFileSelection = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const removeFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
  };

  const downloadArquivo = async (arquivo) => {
    try {
      const response = await apiService.downloadArquivo(arquivo.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', arquivo.nome_original);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao baixar arquivo:', error);
      alert('Erro ao baixar arquivo');
    }
  };

  const deleteArquivo = async (arquivoId) => {
    if (!confirm('Deseja realmente excluir este arquivo?')) return;
    
    try {
      await apiService.deleteArquivo(arquivoId);
      alert('Arquivo excluído com sucesso!');
      carregarArquivos();
    } catch (error) {
      console.error('Erro ao excluir arquivo:', error);
      alert('Erro ao excluir arquivo');
    }
  };

  if (loading) {
    return <div className="p-4 text-center">Carregando documentos...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Foto de Perfil */}
      {showProfilePicture && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-4">
              <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                {profilePicture ? (
                  <img 
                    src={profilePicture.url} 
                    alt="Perfil" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Camera className="h-8 w-8 text-gray-400" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="font-medium">Foto de Perfil</h3>
                <p className="text-sm text-gray-500 mb-2">
                  {profilePicture ? 'Foto de perfil atual' : 'Use o botão abaixo para adicionar foto de perfil'}
                </p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    if (e.target.files && e.target.files[0]) {
                      setSelectedFiles([e.target.files[0]]);
                      setTipoDocumento('Foto');
                      setDescricao('Foto de perfil');
                      await handleFileUpload();
                      e.target.value = ''; // Reset input
                    }
                  }}
                  style={{ display: 'none' }}
                  id="profile-photo-input"
                />
                <label htmlFor="profile-photo-input">
                  <Button size="sm" variant="outline" asChild>
                    <span className="cursor-pointer">
                      <Camera className="h-4 w-4 mr-2" />
                      {profilePicture ? 'Alterar Foto' : 'Adicionar Foto'}
                    </span>
                  </Button>
                </label>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de Arquivos */}
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-medium">Documentos e Fotos</h3>
            <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Enviar Arquivo</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Tipo de Documento</Label>
                    <Select value={tipoDocumento} onValueChange={setTipoDocumento}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        {allowedTypes.map(tipo => (
                          <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Descrição (Opcional)</Label>
                    <Input
                      value={descricao}
                      onChange={(e) => setDescricao(e.target.value)}
                      placeholder="Descrição do documento"
                    />
                  </div>
                  
                  <div>
                    <Label>Arquivos</Label>
                    <Input
                      type="file"
                      multiple
                      onChange={handleFileSelection}
                      accept="image/*,application/pdf,.doc,.docx"
                    />
                  </div>
                  
                  {selectedFiles.length > 0 && (
                    <div className="space-y-2">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="flex justify-between items-center p-2 border rounded">
                          <span className="text-sm">{file.name}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => removeFile(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" onClick={() => setShowUploadModal(false)}>
                      Cancelar
                    </Button>
                    <Button onClick={handleFileUpload} disabled={uploading}>
                      {uploading ? 'Enviando...' : 'Enviar'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          
          {arquivos.filter(arquivo => !arquivo.is_profile).length === 0 ? (
            <p className="text-gray-500 text-center py-4">Nenhum documento enviado</p>
          ) : (
            <div className="space-y-2">
              {arquivos.filter(arquivo => !arquivo.is_profile).map((arquivo) => (
                <div key={arquivo.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    {arquivo.tipo === 'Foto' ? (
                      <ImageIcon className="h-5 w-5 text-blue-500" />
                    ) : (
                      <FileText className="h-5 w-5 text-gray-500" />
                    )}
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{arquivo.nome_original}</span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {arquivo.tipo} • {new Date(arquivo.data_upload).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {arquivo.tipo === 'Foto' && showProfilePicture && !profilePicture && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setAsProfilePicture(arquivo)}
                        title="Definir como foto de perfil"
                      >
                        <Camera className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadArquivo(arquivo)}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteArquivo(arquivo.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};